import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ShieldCheck, Download } from "lucide-react";
import { CONTACT, NAV, CERTS } from "../../data/site";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { DownloadDialog } from "./DownloadDialog";

const CtaBand = () => (
  <div className="bg-gradient-to-r from-brand-50 to-[#E6FBF8] border-t border-slate-200">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10 py-14 lg:py-16 grid gap-8 lg:grid-cols-[1.3fr_1fr] items-center">
      <div>
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-navy">Have a requirement?</h2>
        <p className="mt-3 text-slate-600 text-base md:text-lg">Share your specifications, volumes, or request samples. Our team will respond with next steps.</p>
      </div>
      <div className="flex flex-wrap gap-3 lg:justify-end">
        <a href={`tel:${CONTACT.phones[0].tel}`} data-testid="cta-call-button" className="btn bg-navy text-white hover:bg-navy-800"><Phone className="h-4 w-4" /> Call us</a>
        <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-button" className="btn bg-[#128C7E] text-white hover:brightness-95"><WhatsAppIcon className="h-4 w-4" /> WhatsApp</a>
        <a href={`mailto:${CONTACT.emails[0]}`} data-testid="cta-email-button" className="btn border border-navy/25 bg-white text-navy hover:border-navy"><Mail className="h-4 w-4" /> Email</a>
      </div>
    </div>
  </div>
);

export const Footer = () => (
  <footer data-testid="site-footer">
    <CtaBand />
    <div className="bg-white text-slate-600 border-t border-slate-200">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1.2fr_1.2fr]">
        <div>
          <div className="inline-block"><img src="/img/logo.webp" alt="MU Plast" className="h-12 w-auto" /></div>
          <p className="mt-6 text-sm leading-relaxed max-w-xs">Premium plastic closures and PET preforms for the packaged drinking water and beverage sector. Shrirampur, Maharashtra — since 2022.</p>
          <div className="mt-6 flex flex-wrap gap-2" data-testid="footer-cert-badges">
            {CERTS.map((c) => (
              <DownloadDialog key={c.code} resource={c.resource}>
                <button data-testid={`footer-cert-${c.resource}`} title={`Download ${c.code} certificate`}
                  className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-mist px-3 py-2 text-left transition-colors hover:border-brand hover:bg-white">
                  <ShieldCheck className="h-5 w-5 text-brand" />
                  <div className="leading-tight">
                    <p className="text-xs font-semibold text-navy">{c.code}</p>
                    <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-brand">Certified <Download className="h-3 w-3" /></p>
                  </div>
                </button>
              </DownloadDialog>
            ))}
          </div>
        </div>
        <div>
          <p className="footer-title">Company</p>
          <ul className="mt-5 space-y-3 text-sm">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-brand transition-colors" data-testid={`footer-link-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{n.label}</Link></li>
            ))}
            <li><Link to="/technology-quality#downloads" className="hover:text-brand transition-colors" data-testid="footer-link-downloads">Certificates & brochure</Link></li>
          </ul>
        </div>
        <div>
          <p className="footer-title">Get in touch</p>
          <ul className="mt-5 space-y-3 text-sm">
            {CONTACT.phones.map((p) => (
              <li key={p.tel}><a href={`tel:${p.tel}`} className="flex items-center gap-2 hover:text-brand transition-colors"><Phone className="h-4 w-4 text-brand" />{p.label}</a></li>
            ))}
            {CONTACT.emails.map((e) => (
              <li key={e}><a href={`mailto:${e}`} className="flex items-center gap-2 hover:text-brand transition-colors break-all"><Mail className="h-4 w-4 shrink-0 text-brand" />{e}</a></li>
            ))}
            <li><a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="footer-whatsapp-link" className="flex items-center gap-2 hover:text-brand transition-colors"><WhatsAppIcon className="h-4 w-4 text-[#128C7E]" />WhatsApp us</a></li>
          </ul>
        </div>
        <div>
          <p className="footer-title">Plant address</p>
          <p className="mt-5 flex gap-2 text-sm leading-relaxed"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-brand" /><span>{CONTACT.address.map((l) => <span key={l} className="block">{l}</span>)}</span></p>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10 py-6 flex flex-col sm:flex-row justify-between gap-2 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} MU Plast Pvt Ltd. All rights reserved.</p>
          <p>ISO 9001:2015 & FSSC 22000 certified manufacturing · Shrirampur, Maharashtra</p>
        </div>
      </div>
    </div>
  </footer>
);
